import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Bell, SignOut, ClipboardText, ChartLineUp, BookOpen, AddressBook, Key, House } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardLayout = ({ children }) => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [emailPref, setEmailPref] = useState(true);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { toast.error('New passwords do not match'); return; }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: pwForm.current, new_password: pwForm.next },
        { headers: getAuthHeader() });
      toast.success('Password changed successfully');
      setPwDialogOpen(false);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    if (user) setEmailPref(user.email_notifications_enabled !== false);
  }, [user]);

  const toggleEmailPref = async (checked) => {
    setEmailPref(checked);
    try {
      await axios.put(`${API}/users/me/preferences`,
        { email_notifications_enabled: checked },
        { headers: getAuthHeader() }
      );
      toast.success(checked ? 'Email notifications turned on' : 'Email notifications turned off — in-app alerts stay on');
    } catch (error) {
      setEmailPref(!checked);
      toast.error('Failed to update preference');
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { headers: getAuthHeader() });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/mark-read`, {}, { headers: getAuthHeader() });
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getRoleLabel = (role) => {
    const roleMap = {
      admin: 'Admin',
      employee: 'Employee',
      lab_manager: 'Lab Manager',
      operations_manager: 'Operations Manager',
      finance: 'Finance'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-[100] bg-white border-b border-border-default">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="header-dashboard-link" className="block hover:opacity-80 transition-opacity">
              <h1 className="text-2xl font-bold text-primary-default" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Reagent Procurement
              </h1>
              <p className="text-sm text-text-tertiary">Biotech Workflow Management</p>
            </Link>

            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-sm text-text-secondary hover:text-accent-brand flex items-center gap-1"
                data-testid="dashboard-link"
              >
                <House size={18} weight="duotone" />
                <span className="hidden md:inline">Dashboard</span>
              </Link>

              {(user?.role === 'admin' || user?.role === 'operations_manager') && (
                <Link
                  to="/analytics"
                  className="text-sm text-text-secondary hover:text-accent-brand flex items-center gap-1"
                  data-testid="analytics-link"
                >
                  <ChartLineUp size={18} weight="duotone" />
                  <span className="hidden md:inline">Analytics</span>
                </Link>
              )}

              {(user?.role === 'lab_manager' || user?.role === 'operations_manager') && (
                <Link
                  to="/vendors"
                  className="text-sm text-text-secondary hover:text-accent-brand flex items-center gap-1"
                  data-testid="vendors-link"
                >
                  <AddressBook size={18} weight="duotone" />
                  <span className="hidden md:inline">Vendors</span>
                </Link>
              )}

              <Link
                to="/audit-trail"
                className="text-sm text-text-secondary hover:text-accent-brand flex items-center gap-1"
                data-testid="audit-trail-link"
              >
                <ClipboardText size={18} weight="duotone" />
                <span className="hidden md:inline">Audit Trail</span>
              </Link>

              <Link
                to="/training"
                className="text-sm text-text-secondary hover:text-accent-brand flex items-center gap-1"
                data-testid="training-link"
              >
                <BookOpen size={18} weight="duotone" />
                <span className="hidden md:inline">Training</span>
              </Link>

              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="relative"
                    data-testid="notifications-button"
                  >
                    <Bell size={20} weight="duotone" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-status-destructive text-white text-xs rounded-full flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-2">
                    <h3 className="font-semibold text-text-primary">Notifications</h3>
                    <ScrollArea className="h-96">
                      <div className="space-y-2">
                        {notifications.length === 0 ? (
                          <p className="text-sm text-text-tertiary py-4 text-center">No notifications</p>
                        ) : (
                          notifications.map((notif) => (
                            <div
                              key={notif.id}
                              className={`p-3 rounded border ${
                                notif.read ? 'bg-white border-border-default' : 'bg-accent-brand-subtle border-accent-brand'
                              } cursor-pointer transition-colors hover:bg-surface`}
                              onClick={() => !notif.read && markAsRead(notif.id)}
                              data-testid={`notification-${notif.id}`}
                            >
                              <p className="text-sm text-text-primary">{notif.message}</p>
                              <p className="text-xs text-text-tertiary mt-1">
                                {new Date(notif.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                    <div className="flex items-center justify-between pt-3 border-t border-border-default" data-testid="email-pref-row">
                      <div>
                        <p className="text-sm font-medium text-text-primary">Email notifications</p>
                        <p className="text-xs text-text-tertiary">In-app alerts always stay on</p>
                      </div>
                      <Switch
                        checked={emailPref}
                        onCheckedChange={toggleEmailPref}
                        data-testid="email-notifications-toggle"
                      />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* User Menu */}
              <div className="flex items-center gap-3 pl-4 border-l border-border-default">
                <div className="text-right">
                  <p className="font-medium text-text-primary">{user?.name}</p>
                  <p className="text-xs text-text-tertiary">{getRoleLabel(user?.role)}</p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPwDialogOpen(true)}
                  title="Change password"
                  data-testid="change-password-button"
                >
                  <Key size={20} weight="duotone" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <SignOut size={20} weight="duotone" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Change Password Dialog */}
      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pw-current">Current Password</Label>
              <Input
                id="pw-current"
                type="password"
                value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-next">New Password</Label>
              <Input
                id="pw-next"
                type="password"
                value={pwForm.next}
                onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-confirm">Confirm New Password</Label>
              <Input
                id="pw-confirm"
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={pwSaving}
              className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white"
              data-testid="submit-change-password-button"
            >
              {pwSaving ? 'Saving…' : 'Change Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="p-6 md:p-8 lg:p-12">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
